import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../../hooks/store/useStore';
import { useGetMyOwnTractorImagesById } from '../../../../hooks/api/user/queries/useGetMyOwnTractorImagesById';
import { useCheckCameraPermission } from '../../../../hooks/check/useCheckCameraPermission';
import { useNavigate } from 'react-router-dom';
import { tractorImageType } from '../../../../store/userSellTractorSurveySlice';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SlideConfirmModal } from '../../../../components/common/modal/slide/SlideConfirmModal';
import { SellTractorSurveyImageExample } from '../../SellTractorSurvey/SellTractorSurveyTractorRequiredImagePage/components/SellTractorSurveyImageExample';
import styled from 'styled-components';
import { OwnTractorSurveyPageWrapper } from '../OwnTractorSurveyPageWrapper';
import { usePostMyOwnTractorImages } from '../../../../hooks/api/user/mutations/usePostMyOwnTractorImages.mutation';
import { usePatchMyOwnTractorImages } from '../../../../hooks/api/user/mutations/usePatchMyOwnTractorImages.mutation';
import { IOwnTractorImage } from '../../../../api/user/mutations/postMyOwnTractorImages';
import { AxiosError } from 'axios';
import { tractorImageUploadButtonConstants } from '../../../../constants/tractorImageUploadButton/tractorImageUploadButton.constant';
import { ImageUploadButton } from '../../../../components/common/buttons/ImageUploadButton';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { ampli } from '../../../../ampli';
import ReactGA from 'react-ga4';
import { usePatchFlarelaneUserTags } from '../../../../hooks/api/flarelane/usePatchFlarelaneUserTags';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { useQueryClient } from '@tanstack/react-query';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { Icon } from '../../../../components/common/icon/Icon';
import { MainHead } from '../../../../components/common/head/MainHead';

export const OwnTractorSurveyImageUploadPage = () => {
  const OWN_TRACTOR_IMAGE_CATEGORY = 'own-tractors';

  const shouldOpenSettings = true;
  useCheckCameraPermission(shouldOpenSettings);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const [isCloseButtonClicked, setIsCloseButtonClicked] = useState<boolean>(false);
  const [isRerequestPopupModalOpen, setIsRerequestPopupModalOpen] = useState<boolean>(false);
  const [isSyetemErrorPopupModalOpen, setIsSyetemErrorPopupModalOpen] = useState<boolean>(false);
  const [isImageUploadFailedModalOpen, setIsImageUploadFailedModalOpen] = useState<boolean>(false);

  const [tractorImages, setTractorImages] = useState<IOwnTractorImage[]>([]);

  const isTractorImageExist = useMemo<boolean>(() => {
    return tractorImages && tractorImages.length > 0;
  }, [tractorImages]);

  const userOwnTractorState = useAppSelector((state) => state.userOwnTractorSurvey);

  const { data: myOwnTractorImages } = useGetMyOwnTractorImagesById({
    ownTractorId: userOwnTractorState.ownTractorIdForEdit ?? '',
  });

  const { userProfile } = useGetUserProfile();
  const { mutate: patchFlarelaneUserTags } = usePatchFlarelaneUserTags();

  useEffect(() => {
    if (myOwnTractorImages && myOwnTractorImages.length > 0) {
      setTractorImages(myOwnTractorImages);
    }
  }, [myOwnTractorImages]);

  const isFirstUpload: boolean = myOwnTractorImages?.length === 0;

  const handleSubmitButtonClick = () => {
    if (isFirstUpload) {
      postImageUpload();
    } else {
      patchImageUpload();
    }
  };

  const { mutate: postMyOwnTractorImages, isLoading: isPostMyOwnTractorImagesLoading } = usePostMyOwnTractorImages();
  const { mutate: patchMyOwnTractorImages, isLoading: isPatchMyOwnTractorImagesLoading } = usePatchMyOwnTractorImages();

  const [isShowExampleImageModal, setIsShowExampleImageModal] = useState<boolean>(false);
  const [isExampleModalShowed, setIsExampleModalShowed] = useState<boolean>(false);
  const [isCompleteModalShowed, setIsCompleteModalShowed] = useState<boolean>(false);

  const representiveImageRef = useRef<HTMLInputElement | null>(null);

  const handleSuccess = () => {
    ampli.pvmSubmitOwnTractorImage();
    ReactGA.event('pvm_submit_own_tractor_image');
    queryClient.invalidateQueries({ queryKey: ['my_own_tractor_image_exists'] });
    setIsCompleteModalShowed(true);
  };

  const handleError = (error: unknown) => {
    if (error instanceof AxiosError) {
      if (!error.response) {
        setIsRerequestPopupModalOpen(true);
      } else {
        setIsSyetemErrorPopupModalOpen(true);
      }
    }
  };

  const postImageUpload = () => {
    postMyOwnTractorImages(
      {
        reqBody: {
          ownTractorId: userOwnTractorState.ownTractorIdForEdit ?? '',
          images: tractorImages.map((image) => ({
            type: image.type,
            url: image.url,
          })),
        },
      },
      {
        onSuccess: handleSuccess,
        onError: handleError,
      },
    );
  };

  const patchImageUpload = () => {
    patchMyOwnTractorImages(
      {
        reqBody: {
          ownTractorId: userOwnTractorState.ownTractorIdForEdit ?? '',
          images: tractorImages.map((image) => ({
            type: image.type,
            url: image.url,
          })),
        },
      },
      {
        onSuccess: handleSuccess,
        onError: handleError,
      },
    );
  };
  const handleUploadImage = (imageType: tractorImageType, imageUrl: string) => {
    ampli.pvmUploadOwnTractorImage();
    setTractorImages((prevImages) => [...prevImages, { type: imageType, url: imageUrl }]);
  };

  const handleDeleteImage = (imageType: tractorImageType) => {
    setTractorImages((prevImages) => {
      const newImages = prevImages.filter((image) => image.type !== imageType);
      return newImages;
    });
  };

  const handleImageExampleModalTriggerClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isExampleModalShowed) {
      setIsShowExampleImageModal(true);
      setIsExampleModalShowed(true);
      e.preventDefault();
    }
  };

  const handleImageUploadError = () => setIsImageUploadFailedModalOpen(true);

  useLayout({
    fixedHeader: (
      <MainHead backButton={{ isShow: false }} closeButton={{ closeHandler: () => setIsCloseButtonClicked(true) }} />
    ),
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        <ConfirmButton type={'button'} disabled={!isTractorImageExist} onClick={handleSubmitButtonClick}>
          <ConfirmButtonText>{'완료'}</ConfirmButtonText>
        </ConfirmButton>
      </ButtonSection>
    ),
    fixedHeaderHeight: 48,
    fixedFooterHeight: 72,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (userProfile) {
      patchFlarelaneUserTags({ lastVisitedOwnTractorImageUploadPage: new Date().toISOString() });
    }
  }, [userProfile, patchFlarelaneUserTags]);

  useEffect(() => {
    // TODO : isOwnTractorImageEditStartedProperly 로 분리 (https://gint-team.atlassian.net/browse/PVM-2913)
    if (!userOwnTractorState.isOwnTractorSurveyStartedProperly) {
      navigate(pageInfo.myInfoOwnTractorListPage.path, { replace: true });
      openNotification({ message: '비정상적인 접근입니다.' });
    }
  }, [userOwnTractorState.isOwnTractorSurveyStartedProperly, navigate, openNotification]);

  return (
    <>
      <>
        <HeaderSection>
          <Header>{'보유하신 트랙터의\n사진을 등록해주세요'}</Header>
        </HeaderSection>

        <DescriptionSection>
          <DescriptionSectionInnerContainer>
            <DescriptionTextContainer>
              <DescriptionTitle>{'📷 사진 등록으로 내 트랙터 주목도 UP!'}</DescriptionTitle>
              <DescriptionContent>{'가격 제안도 받고, 높은 관심을 받아보세요!'}</DescriptionContent>
            </DescriptionTextContainer>
          </DescriptionSectionInnerContainer>
        </DescriptionSection>
        <SurveyForm id={'survey_page_wrapper'}>
          <ContentsContainer>
            <SurveyInputHeader inputTitle={'매물 대표 사진'} isOptional />

            <ImageUploadSection>
              <ImageExampleModalTrigger onClick={handleImageExampleModalTriggerClick}>
                {tractorImageUploadButtonConstants
                  .filter((tractorImageUploadButton) => tractorImageUploadButton.type === 'LEFT')
                  .map((tractorImageUploadButton) => (
                    <ImageUploadButton
                      key={tractorImageUploadButton.id}
                      inputId={tractorImageUploadButton.id}
                      buttonText={tractorImageUploadButton.buttonText}
                      uploadedImageUrl={
                        tractorImages.find((image) => image.type === tractorImageUploadButton.type)?.url ?? ''
                      }
                      successHandler={(imageUrl: string) => handleUploadImage(tractorImageUploadButton.type, imageUrl)}
                      errorHandler={handleImageUploadError}
                      deleteHandler={() => handleDeleteImage(tractorImageUploadButton.type)}
                      inputRef={representiveImageRef}
                      category={OWN_TRACTOR_IMAGE_CATEGORY}
                    />
                  ))}
              </ImageExampleModalTrigger>
            </ImageUploadSection>
          </ContentsContainer>

          <ContentsContainer>
            <SurveyInputHeader inputTitle={'매물 추가 사진'} isOptional />
            <ImageUploadSection>
              {tractorImageUploadButtonConstants
                .filter((tractorImageUploadButton) => tractorImageUploadButton.type !== 'LEFT')
                .map((tractorImageUploadButton) => (
                  <ImageUploadButton
                    key={tractorImageUploadButton.id}
                    inputId={tractorImageUploadButton.id}
                    buttonText={tractorImageUploadButton.buttonText}
                    uploadedImageUrl={
                      tractorImages.find((image) => image.type === tractorImageUploadButton.type)?.url ?? ''
                    }
                    successHandler={(imageUrl: string) => handleUploadImage(tractorImageUploadButton.type, imageUrl)}
                    errorHandler={handleImageUploadError}
                    deleteHandler={() => handleDeleteImage(tractorImageUploadButton.type)}
                    category={OWN_TRACTOR_IMAGE_CATEGORY}
                  />
                ))}
            </ImageUploadSection>
          </ContentsContainer>
        </SurveyForm>
      </>

      <PopupConfirmModal
        isShow={isRerequestPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsRerequestPopupModalOpen(false)}
        description={'요청 시간이 초과되었어요 🙂\n아래 버튼을 눌러 다시 요청해주세요!'}
        buttonArray={[
          {
            text: '등록 완료 하기',
            onClick: handleSubmitButtonClick,
            color: 'blue',
            isLoading: isPostMyOwnTractorImagesLoading || isPatchMyOwnTractorImagesLoading,
            disabled: isPostMyOwnTractorImagesLoading || isPatchMyOwnTractorImagesLoading,
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isSyetemErrorPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsSyetemErrorPopupModalOpen(false)}
        description={'시스템 에러로 등록이 불가능합니다👨🏻‍🔧\n잠시후에 다시 이용해주세요.'}
        buttonArray={[
          {
            text: '홈으로',
            onClick: () => navigate(pageInfo.homePage.path, { replace: true }),
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isImageUploadFailedModalOpen}
        hasCloseButton={false}
        title={'통신이 원활하지 못해요 👨🏻‍🔧'}
        description={'통신 환경이 더 원활한 곳에서\n다시 시도 해주세요'}
        buttonArray={[
          {
            text: '홈으로',
            onClick: () => navigate(pageInfo.homePage.path, { replace: true }),
            color: 'blue',
          },
        ]}
      />
      <SlideConfirmModal
        isShow={isShowExampleImageModal}
        height={553}
        element={<SellTractorSurveyImageExample />}
        confirmButton={{
          handleClick: () => {
            setIsShowExampleImageModal(false);
            representiveImageRef.current?.click();
          },
          text: '확인',
        }}
        closeButton={{
          handleClick: () => setIsShowExampleImageModal(false),
        }}
      />
      <PopupConfirmModal
        isShow={isCloseButtonClicked}
        hasCloseButton={false}
        closeModal={() => {}}
        title={isTractorImageExist ? '사진 등록 완료하기' : '정말 종료 하시겠어요?'}
        description={
          isTractorImageExist ? '등록하신 사진을\n저장하고 나가시겠어요?' : '대표 사진 1장만 있으면 충분해요!'
        }
        buttonArray={[
          {
            text: isTractorImageExist ? '저장 후 나가기' : '등록하기',
            onClick: () => {
              setIsCloseButtonClicked(false);
              if (isTractorImageExist) {
                handleSubmitButtonClick();
              }
            },
            color: 'blue',
          },
          {
            text: '나가기',
            color: 'gray',
            onClick: () => navigate(pageInfo.myInfoOwnTractorListPage.path, { replace: true }),
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isCompleteModalShowed}
        hasCloseButton={false}
        closeModal={() => {}}
        title={'사진 등록 완료! 🎉'}
        buttonArray={[
          {
            text: '보유트랙터 관리로 가기',
            onClick: () => navigate(pageInfo.myInfoOwnTractorListPage.path, { replace: true }),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const ContentsContainer = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const ImageUploadSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const ImageExampleModalTrigger = styled.div`
  width: 100%;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;

  padding: 0px 16px 24px 16px;
`;

const DescriptionSectionInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  padding: 24px;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const DescriptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;

  padding: 0px 6px;
`;

const DescriptionTitle = styled.span`
  align-self: stretch;

  color: var(--Light-Scheme-Color-primary-blue, #196eff);
  text-align: center;

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const DescriptionContent = styled.span`
  align-self: stretch;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const CloseButtonSection = styled.div`
  width: 100%;
  height: 36px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  padding: 0px 16px;
`;

const CloseButton = styled.button``;

const HeaderSection = styled.div`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Header = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  word-break: break-all;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const ConfirmButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;
